import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ExtendedFormlyFieldConfig, StepType } from '@app/forms/config/form-model';
import { AssmatAvailability, AssmatAvailabilityAgrements, AssmatComplement, AssmatForm, AssmatFormation, AssmatFormationProgramme, AssmatModeAccueil, MAM } from '@app/models/ass-mat';
import { ConfigMentalo } from '@app/models/mentalo';
import { BaseConfigService, FormConfigService, PermissionService, SnackbarService, UserService } from '@app/services';
import { AssmatService } from '@app/services/assmat.service';
import { dateInPeriod } from '@app/utils/date-functions';
import moment, { Moment } from 'moment';
import { forkJoin, Observable } from 'rxjs';

@Component({
  selector: 'app-assmat-info',
  templateUrl: './assmat-info.component.html',
  styleUrls: ['./assmat-info.component.scss'],
  standalone: false
})
export class AssmatInfoComponent implements OnInit {

  // Data
  idAssmat: number;
  loading: boolean = true;
  assmatData: any;
  modeAccueil: AssmatModeAccueil;
  complement: AssmatComplement;
  listMAM: MAM[];
  selectedMAM: MAM;
  idMAM: number;
  labelMAM: string;
  modeAccueilList: string[] = [];
  valeursLibres: any[] = [];
  availability: AssmatAvailability[];
  permToEdit: boolean;
  permToDisplayDisponibilites: boolean;
  permToDisplayFormations: boolean;
  listValeursLibres: any[] = [];
  configMentalo: ConfigMentalo;
  formations: AssmatFormation[];
  // Step names
  assmatFormSteps: any[];

  constructor(
    private router: Router,
    private userService: UserService,
    private formConfigService: FormConfigService,
    private assmatService: AssmatService,
    private permService: PermissionService,
    private configService: BaseConfigService,
    private snackBarService: SnackbarService
  ) { }

  ngOnInit(): void {
    this.idAssmat = this.userService.currentUser.idAssmat || null;
    this.permToEdit = this.permService.hasPermission('informations_assmat_edit');
    this.permToDisplayDisponibilites = this.permService.hasPermission('disponibilites_edit');
    this.permToDisplayFormations = this.permService.hasPermission('formations_edit');
    this.loadAssmatData();
  }

  loadAssmatData(): void {

    const observables: {
      assmatForm: Observable<AssmatForm>,
      config: Observable<ConfigMentalo>,
      listMAM: Observable<MAM[]>,
      availability?: Observable<AssmatAvailabilityAgrements>,
      formations?: Observable<AssmatFormationProgramme>
    } = {
      assmatForm: this.assmatService.getAssmatById(this.idAssmat),
      config: this.configService.get<ConfigMentalo>('mentalo'),
      listMAM: this.assmatService.getListMAM(this.idAssmat)
    };

    if (this.permToDisplayDisponibilites) {
      observables.availability = this.assmatService.getAvailability(this.idAssmat);
    }

    if (this.permToDisplayFormations) {
      observables.formations = this.assmatService.getFormations(this.userService.currentUser.idAssmat);
    }

    this.loading = true;

    forkJoin(observables).subscribe({
      next: ({ assmatForm, config, listMAM, availability, formations }) => {
        this.assmatData = assmatForm.data;
        this.configMentalo = config;
        this.listMAM = listMAM;
        this.modeAccueil = this.assmatData.Bloc_mode_accueil;
        this.complement = this.assmatData.Bloc_complement;
        this.idMAM = this.assmatData.MAM;
        this.valeursLibres = this.assmatData.Bloc_libre;

        if (availability) {
          this.availability = availability.disponibilites.map(dispo => ({
            ...dispo,
            active: dateInPeriod(dispo.dateDebut, dispo.dateFin)
          }));
        }

        if (formations) {
          this.formations = formations.formations;
        }

        this.assmatFormSteps = this.getFormattedSteps(assmatForm.config);
        this.listValeursLibres = this.getFormattedValeursLibres(assmatForm.config);
        this.selectedMAM = this.listMAM.find(mam => mam.idMAM === this.idMAM);
      },
      error: (err) => {
        this.snackBarService.error('Erreur lors du chargement des données')
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  getFormattedSteps(steps: StepType[], excludedNames?: string[]) {
    return steps
      .filter(step => !excludedNames || !excludedNames.includes(step.stepName))
      .map(step => ({ stepName: step.stepName, label: step.label }));
  }

  getFormattedValeursLibres(form: StepType[]) {
    let valeursLibresFieldsConfig: ExtendedFormlyFieldConfig[] = [];
    let formattedValeursLibres: any[] = [];
    let valeursLibresStep = form.find(formStep => formStep.stepName === "valeursLibres");

    if (valeursLibresStep) {
      let listKeys = this.formConfigService.getKeysListOfOneStep(valeursLibresStep);

      listKeys.forEach(key => {
        valeursLibresFieldsConfig.push(this.formConfigService.findFieldByName(valeursLibresStep.fields, key));
      });

      let mapValeursLibresFieldsConfig = valeursLibresFieldsConfig.map(val => {
        return { key: val.key, label: val.templateOptions.label, type: val.type, options: val.templateOptions.options };
      }).filter(val => val.key !== "message");

      mapValeursLibresFieldsConfig.forEach(val => {
        for (const key in this.valeursLibres) {

          if (Object.prototype.hasOwnProperty.call(this.valeursLibres, key)) {
            let value = this.valeursLibres[key];

            if (key === val.key) {

              if (val.options && value) {
                const options = val.options as any[];
                value = options.find(opt => opt.value === value)?.label;
              }

              if (val.type === 'datepicker') {
                const m = moment(value, "YYYYMMDD");
                value = m.isValid() ? m.toDate() : null;
              }

              if (value) {
                formattedValeursLibres.push({ label: val.label, type: val.type, value });
              }

            }
          }
        }
      });
      return formattedValeursLibres;
    }
  }

  openForm(stepName) {
    const path = `/account/assmat/${stepName}`;
    this.router.navigate([path]);
  }

}
